import React, { useEffect, useState } from 'react';
import SchedulerWorker from '../SchedulerWorker';
import CompactClosedChair, { COMPACT_CLOSED_CHAIR_WIDTH } from '../CompactClosedChair';
import styles from './SchedulerDay.module.scss';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import {
  DAY_HEADER_HEIGHT,
  MAJOR_TICK_MINUTES,
  MINOR_TICK_MINUTES,
  WORKER_HEADER_HEIGHT,
  getCalendarTicks,
  getTickOffset,
  getWorkerHeaderOffset,
  scaleLayoutPx,
} from '../../constants/layout';

const SchedulerDay = ({
  calendarItemsCount,
  day,
  cardSpace,
  setStartData,
  startDataRef,
  stepSize,
  startData,
  numberOfActiveDays,
  containerRef,
  startWorkTime,
  endWorkTime,
  onExpandClosedChair,
  zoomScale = 1,
}) => {
  const showHeader = numberOfActiveDays > 1;
  const headerOffset = getWorkerHeaderOffset(showHeader, zoomScale);
  const dayHeaderHeight = scaleLayoutPx(DAY_HEADER_HEIGHT, zoomScale);
  const workerHeaderHeight = scaleLayoutPx(WORKER_HEADER_HEIGHT, zoomScale);
  const majorTimeTicks = getCalendarTicks(startWorkTime, endWorkTime, MAJOR_TICK_MINUTES, {
    includeEnd: true,
  });
  const minorTimeTicks = getCalendarTicks(startWorkTime, endWorkTime, MINOR_TICK_MINUTES).filter(
    (tick) => tick % MAJOR_TICK_MINUTES !== 0,
  );
  const isPresentDay = day.startWork < dayjs().unix() && day.endWork > dayjs().unix();
  const isPastDay = !isPresentDay && day.startWork < dayjs().unix();
  const countPosition = () =>
    Math.ceil((dayjs().unix() - day.startWork) / (day.workTime / calendarItemsCount)) * stepSize;
  const [presentDayPosition, setPresentDayPosition] = useState(isPresentDay ? countPosition() : 0);
  const [scrolled, setScrolled] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const compactChairWidth = scaleLayoutPx(COMPACT_CLOSED_CHAIR_WIDTH, zoomScale, { min: 48 });
  const dayMinWidth = (day.workers || []).reduce(
    (total, worker) => total + (worker.chairCompact ? compactChairWidth : cardSpace),
    0,
  );
  const visibleChairs = (day.workers || []).filter((w) => !w.chairCompact).length;
  const singleChair = visibleChairs === 1;

  useEffect(() => {
    if (!isPresentDay) return;
    const intervalId = setInterval(() => {
      const position = countPosition();
      setPresentDayPosition((prev) => (prev !== position ? position : prev));
    }, 60000);
    return () => clearInterval(intervalId);
  }, [isPresentDay, day.startWork, day.workTime, stepSize, calendarItemsCount]);
  const scrollPage = () => {
    if (isPresentDay && !scrolled) {
      containerRef.current.scrollTo(0, presentDayPosition - 200);
      setScrolled(true);
    }
    if (searchParams.get('reset') == 2) {
      containerRef.current.scrollTo(0, presentDayPosition - 200);
      searchParams.set('reset', 0);
      setSearchParams(searchParams);
    }
  };
  useEffect(scrollPage, [presentDayPosition, searchParams]);
  return (
    <div
      className={styles.scheduler__day__wrapper}
      style={{
        minWidth: `${dayMinWidth}px`,
        '--chair-min-width': `${cardSpace}px`,
        '--calendar-zoom': zoomScale,
        '--day-header-height': `${dayHeaderHeight}px`,
        '--worker-header-height': `${workerHeaderHeight}px`,
      }}
    >
      {showHeader && <div className={styles.day__header}>{dayjs(day.date).format('dddd DD')}</div>}{' '}
      <div className={styles.day_time_line_wrapper}>
        {minorTimeTicks.map((tick) => (
          <div
            key={`minor-${tick}`}
            className={`${styles.day_time_line} ${styles.day_time_line_minor}`}
            style={{ top: `${getTickOffset(tick, startWorkTime, stepSize, headerOffset)}px` }}
          />
        ))}
        {majorTimeTicks.map((tick, index) => (
          <div
            key={tick}
            className={
              index === 0
                ? `${styles.day_time_line} ${styles.day_time_line_start}`
                : styles.day_time_line
            }
            style={{ top: `${getTickOffset(tick, startWorkTime, stepSize, headerOffset)}px` }}
          />
        ))}
      </div>
      {isPresentDay ? (
        <div
          className={`${styles.day_blocked} ${styles.day_now}`}
          style={{
            height: `${presentDayPosition}px`,
            top: `${headerOffset}px`,
          }}
        />
      ) : isPastDay ? (
        <div
          className={styles.day_blocked}
          style={{
            top: `${headerOffset}px`,
            height: `calc(100% - ${headerOffset}px)`,
          }}
        />
      ) : null}
      <div className={styles.workers_container}>
        {day.workers.map((worker) => {
          if (worker.chairCompact) {
            return (
              <CompactClosedChair
                key={worker.id}
                worker={worker}
                showHeader={showHeader}
                zoomScale={zoomScale}
                onExpand={() => onExpandClosedChair?.(day.date, worker.id)}
              />
            );
          }
          return (
            <SchedulerWorker
              key={worker.id}
              worker={worker}
              day={{
                date: day.date,
                endWork: day.endWork,
                startWork: day.startWork,
                workTime: day.workTime,
              }}
              setStartData={setStartData}
              startDataRef={startDataRef}
              stepSize={stepSize}
              startData={startData}
              showHeader={showHeader}
              singleChair={singleChair}
              cardSpace={cardSpace}
              zoomScale={zoomScale}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SchedulerDay;
